import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Heading } from "../common/Heading/Heading";
import { Button } from "../common/Button/Button";
import { Dropdown } from "../common/Dropdown/Dropdown";
import { LoadingIndicator } from "../common/LoadingIndicator/LoadingIndicator";
import { useData } from "../../context/DataContext";
import { ChatCard } from "./ChatCard/ChatCard";
import { AddChatModal } from "./AddChatModal/AddChatModal";
import { CopyModal } from "./CopyModal/CopyModal";
import { COLORS } from "../../assets/constants/styles";
import axios from "../../api/axiosInstance";
import { useParams, useNavigate } from "react-router-dom";
import { useLogs } from "../../context/LogsContext";

// Chat service function - moved from Test component
const getAnswerResponse = async (
  messages,
  organizationId = null,
  blockName = null,
  blockId = null
) => {
  const chat_id = `test-${Math.floor(100000 + Math.random() * 900000)}`;

  const payload = {
    messages,
    chat_id,
  };

  if (organizationId) {
    payload.orgid = organizationId;
  }

  if (blockName) {
    payload.blockName = blockName;
  }

  if (blockId) {
    payload.blockId = blockId;
  }

  const { data } = await axios.post(`/chat/answer`, payload);
  return data;
};

// Create a utility function for generating guaranteed unique block IDs within PostgreSQL limits
const generateUniqueBlockId = () => {
  const timestamp = Date.now();

  const seconds = Math.floor(timestamp / 1000);

  const random = Math.floor(Math.random() * 10000);

  let uniqueId = seconds * 10000 + random;
  if (uniqueId > 2147483647) {
    uniqueId = uniqueId % 2147483647;

    if (uniqueId <= 0) {
      uniqueId = 1 + random;
    }
  }

  return uniqueId;
};

const parsePromptData = (promptString) => {
  if (!promptString) return [];

  try {
    const promptData = JSON.parse(promptString);
    if (promptData && promptData.messages && promptData.messages.length > 1) {
      // The messages are in the second element (index 1) of the messages array
      const messagesJson = promptData.messages[1].content;

      try {
        const parsedMessages = JSON.parse(messagesJson);
        return Array.isArray(parsedMessages) ? parsedMessages : [];
      } catch (innerErr) {
        // Attempt to extract messages using a different approach
        if (
          typeof messagesJson === "string" &&
          messagesJson.includes('"isClient"')
        ) {
          // Try to extract JSON-like substrings
          const matches = messagesJson.match(/\[.*\]/);
          if (matches && matches[0]) {
            try {
              return JSON.parse(matches[0]);
            } catch (e) {
              // Failed to parse
            }
          }
        }
      }
    }
  } catch (err) {
    // Error parsing prompt data
  }

  return [];
};

export const BlockTestCreate = () => {
  const { t } = useTranslation();
  const { id: blockTestId } = useParams();
  const navigate = useNavigate();
  const {
    organizations,
    fetchOrganizations,
    isLoading: isLoadingOrgs,
  } = useData();
  const { fetchBlockTestAnswers, isLoadingBlockTestDetails } = useLogs();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [chats, setChats] = useState([]);
  const [blockTestName, setBlockTestName] = useState("");
  const [isRunningTests, setIsRunningTests] = useState(false);
  const [completedTests, setCompletedTests] = useState(0);
  const [hasRunTests, setHasRunTests] = useState(false);
  const [isViewMode, setIsViewMode] = useState(!!blockTestId);
  const [isLoadingBlockTest, setIsLoadingBlockTest] = useState(!!blockTestId);
  const [blockId, setBlockId] = useState(null);
  const [blockTestError, setBlockTestError] = useState(null);
  const [selectedChatToCopy, setSelectedChatToCopy] = useState(null);
  const BATCH_SIZE = 10;

  // Only get blockId from URL in view mode, don't generate one on mount
  useEffect(() => {
    if (blockTestId && !isNaN(parseInt(blockTestId))) {
      // Use existing block ID for view mode
      const parsedId = parseInt(blockTestId);
      setBlockId(parsedId);
    }
  }, [blockTestId]);

  useEffect(() => {
    if (!organizations.length) {
      fetchOrganizations(1, 100); // Load all organizations
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Set default organization once loaded
    if (organizations.length > 0 && !selectedOrg) {
      setSelectedOrg(organizations[0].id);
    }
  }, [organizations, selectedOrg]);

  // Fetch block test details if we're in view mode
  useEffect(() => {
    if (blockTestId && !chats.length) {
      setIsViewMode(true);
      setHasRunTests(true);
      setIsLoadingBlockTest(true);

      const fetchBlockTest = async () => {
        try {
          const responses = await fetchBlockTestAnswers(blockTestId);

          if (responses && responses.length > 0) {
            // Set the block test name from the response data (preferred) or fallback to ID
            const blockName = responses[0].block_name || blockTestId;
            setBlockTestName(blockName);

            // Extract organization data from the first answer
            if (responses[0].org_name) {
              // We can't set selected org by name directly since the dropdown uses IDs
              // Instead, find the matching org by name in the organizations list
              const matchingOrg = organizations.find(
                (org) => org.name === responses[0].org_name
              );

              if (matchingOrg) {
                setSelectedOrg(matchingOrg.id);
              }
            }

            // Set block ID from responses if available
            if (responses[0].blockId) {
              setBlockId(responses[0].blockId);
            }

            // Process each response to extract messages from prompt
            const chatData = responses.map((answer) => {
              // Use the helper function to parse prompt and extract messages
              const extractedMessages = parsePromptData(answer.prompt);

              return {
                id: answer.answer_id || Date.now().toString(),
                messages: extractedMessages,
                organizationId: answer.orgid,
                isLoading: false,
                answer: answer.answer_text || "",
                error: answer.error_key ? true : false,
                errorKey: answer.error_key,
                answerId: answer.answer_id,
                blockId: answer.blockId,
                status: answer.status || "undecided",
                chatId: answer.chat_id,
                orgName: answer.org_name,
                blockName: answer.block_name,
                userEmail: answer.user_email,
                summary: answer.summary,
              };
            });

            setChats(chatData);
            setCompletedTests(chatData.length);
            setBlockTestError(null);
          } else {
            setBlockTestError(`No data found for block test: ${blockTestId}`);
          }
        } catch (error) {
          setBlockTestError(`Error loading block test: ${error.message}`);
        } finally {
          setIsLoadingBlockTest(false);
        }
      };

      fetchBlockTest();
    }
  }, [blockTestId, fetchBlockTestAnswers, chats.length, organizations]);

  const handleAddChat = (chatData) => {
    setChats([
      ...chats,
      {
        id: Date.now().toString(),
        messages: chatData.messages,
        isLoading: false,
      },
    ]);
    setIsModalOpen(false);
  };

  const handleRemoveChat = (chatId) => {
    setChats(chats.filter((chat) => chat.id !== chatId));
  };

  const updateChatResult = (chatId, updatedData) => {
    setChats((prevChats) =>
      prevChats.map((chat) =>
        chat.id === chatId ? { ...chat, ...updatedData } : chat
      )
    );
  };

  const getOrganizationOptions = () => {
    return organizations.reduce((acc, org) => {
      acc[org.id] = org.name;
      return acc;
    }, {});
  };

  const handleOrganizationChange = (orgId) => {
    if (!hasRunTests) {
      setSelectedOrg(orgId);
    }
  };

  const runTestForChat = async (chat, testBlockId) => {
    try {
      // Set this specific chat to loading state
      updateChatResult(chat.id, { isLoading: true });

      const response = await getAnswerResponse(
        chat.messages,
        selectedOrg,
        blockTestName,
        testBlockId
      );

      // Create a complete update object
      const updateData = {
        answer: response.answer,
        answerId: response.answer_id,
        isLoading: false, // Clear loading state on success
        errorKey: response.errorKey,
        error: response.error,
      };

      // Update the chat with answer data
      updateChatResult(chat.id, updateData);

      setCompletedTests((prev) => prev + 1);
      return { success: true, chatId: chat.id, data: updateData };
    } catch (error) {
      let updateData = {};

      // If we got an answer_id even though there was an error, try to use it
      if (error.response?.data?.answer_id) {
        const answerId = error.response.data.answer_id;
        const errorKey = error.response?.data?.errorKey || "unknown";

        updateData = {
          answerId,
          error: error.response?.data?.message || "Unknown error",
          errorKey,
          isLoading: false, // Clear loading state on error
        };
      } else {
        // Generic error without an answer ID
        updateData = {
          error: error.message || "Unknown error",
          errorKey: "unknown",
          isLoading: false, // Clear loading state on error
        };
      }

      // Update the chat with error data
      updateChatResult(chat.id, updateData);

      setCompletedTests((prev) => prev + 1);
      return {
        success: false,
        chatId: chat.id,
        error: error.message,
        data: updateData,
      };
    }
  };

  const handleRunTest = async () => {
    if (isRunningTests || chats.length === 0) return;

    try {
      // Generate a new unique block ID each time tests are run
      const newBlockId = generateUniqueBlockId();
      setBlockId(newBlockId);

      setIsRunningTests(true);
      setCompletedTests(0);
      setHasRunTests(true);

      // Process one batch at a time to ensure state updates are synchronized
      for (let i = 0; i < chats.length; i += BATCH_SIZE) {
        const batch = chats.slice(i, i + BATCH_SIZE);

        // Process each chat in the batch individually, but in parallel
        // Pass the newly generated blockId to each chat request
        const batchPromises = batch.map((chat) =>
          runTestForChat(chat, newBlockId)
        );
        await Promise.all(batchPromises);
      }

      // When all batches are done, set overall running state to false
      setIsRunningTests(false);
    } catch (error) {
      setIsRunningTests(false);
    }
  };

  const handleResetTests = () => {
    setChats([]);
    setHasRunTests(false);
    setCompletedTests(0);
    // Reset blockId to null when tests are reset
    setBlockId(null);
  };

  const handleRerunTest = () => {
    // Switch from view mode to edit mode
    setIsViewMode(false);
    // Keep existing chats but reset their results
    setChats((prevChats) =>
      prevChats.map((chat) => ({
        ...chat,
        isLoading: false,
        answer: undefined,
        error: undefined,
        errorKey: undefined,
        answerId: undefined,
        status: "undecided",
      }))
    );
    // Reset test status
    setHasRunTests(false);
    setCompletedTests(0);
    // Clear blockId to generate a new one when test is run
    setBlockId(null);
    // Navigate to create mode without the block test ID
    navigate("/block-test/new");
  };

  const handleCopyChat = (chat) => {
    setSelectedChatToCopy(chat);
    setIsCopyModalOpen(true);
  };

  const handleCreateCopies = (numberOfCopies) => {
    if (!selectedChatToCopy || numberOfCopies < 1) return;

    const newChats = [];
    for (let i = 0; i < numberOfCopies; i++) {
      // Create a deep copy of the messages array to ensure each chat has its own reference
      const messagesCopy = selectedChatToCopy.messages.map((msg) => ({
        ...msg,
      }));

      newChats.push({
        ...selectedChatToCopy,
        id: Date.now().toString() + i, // Ensure each copy has a unique ID
        messages: messagesCopy,
        isLoading: false,
        answer: undefined,
        error: undefined,
        errorKey: undefined,
        answerId: undefined,
      });
    }

    setChats([...chats, ...newChats]);
    setIsCopyModalOpen(false);
    setSelectedChatToCopy(null);
  };

  return (
    <Container>
      <TitleSection>
        <HeadingWrapper>
          <Heading
            text={
              isViewMode
                ? t("test.viewBlockTest", { id: blockId || blockTestId })
                : t("test.createBlockTest")
            }
            size={1}
          />
        </HeadingWrapper>
        <ControlsRow>
          {!isViewMode && !hasRunTests && (
            <Button
              text={
                isRunningTests
                  ? `${t("common.testing")} (${completedTests}/${chats.length})`
                  : t("common.runTest")
              }
              onClick={handleRunTest}
              variant="violet"
              disabled={
                isLoadingOrgs ||
                !selectedOrg ||
                chats.length === 0 ||
                isRunningTests ||
                blockTestName.trim() === ""
              }
            />
          )}
          {!isViewMode && hasRunTests && !isRunningTests && (
            <Button
              text={t("test.resetTests")}
              onClick={handleResetTests}
              variant="white"
              disabled={isRunningTests}
            />
          )}
          {isViewMode && (
            <>
              <Button
                text={t("test.rerunTest")}
                onClick={handleRerunTest}
                variant="violet"
                disabled={isLoadingBlockTest || chats.length === 0}
              />
              <Button
                text={t("common.back")}
                onClick={() => navigate("/block-test")}
                variant="white"
              />
            </>
          )}
        </ControlsRow>
      </TitleSection>

      <FormSection>
        <FormRow>
          <DropdownContainer>
            {isViewMode && chats?.[0]?.orgName ? (
              <>
                <InputLabel>{t("organizations.organization")}</InputLabel>
                <OrgNameDisplay>{chats[0].orgName}</OrgNameDisplay>
              </>
            ) : (
              <Dropdown
                label={t("organizations.selectOrganization")}
                options={getOrganizationOptions()}
                defaultSelected={selectedOrg || ""}
                handleSelect={handleOrganizationChange}
                placeholder={t("organizations.selectOrganization")}
                allowFiltering={true}
                disabled={hasRunTests || isViewMode}
              />
            )}
          </DropdownContainer>
          <NameInputContainer>
            <InputLabel>{t("test.blockTestName")}</InputLabel>
            {isViewMode ? (
              <TestNameDisplay>{blockTestName}</TestNameDisplay>
            ) : (
              <NameInput
                type="text"
                value={blockTestName}
                onChange={(e) => setBlockTestName(e.target.value)}
                placeholder={t("test.enterBlockTestName")}
                disabled={hasRunTests || isViewMode}
              />
            )}
          </NameInputContainer>
        </FormRow>
      </FormSection>

      <SectionTitle>
        {t("test.testChats")}
        {!hasRunTests && !isViewMode && (
          <AddChatButton
            onClick={() => setIsModalOpen(true)}
            disabled={isLoadingOrgs || !selectedOrg || isRunningTests}
          >
            <PlusSign>+</PlusSign> {t("test.addNewChat")}
          </AddChatButton>
        )}
      </SectionTitle>

      {isLoadingBlockTest ? (
        <LoadingIndicator message={t("common.loading")} />
      ) : !organizations.length && isLoadingOrgs ? (
        <LoadingIndicator message={t("common.loading")} />
      ) : (
        <>
          {chats.length === 0 ? (
            <EmptyState>
              <EmptyStateText>{t("test.noChatsAdded")}</EmptyStateText>
              {!isViewMode && (
                <Button
                  text={t("test.addFirstChat")}
                  onClick={() => setIsModalOpen(true)}
                  variant="violet"
                  disabled={
                    isLoadingOrgs ||
                    !selectedOrg ||
                    isRunningTests ||
                    hasRunTests
                  }
                />
              )}
            </EmptyState>
          ) : (
            <ChatGrid>
              {chats.map((chat, index) => (
                <ChatCard
                  key={chat.id}
                  chat={chat}
                  index={index}
                  onRemove={() => handleRemoveChat(chat.id)}
                  onCopy={handleCopyChat}
                  updateChatResult={updateChatResult}
                  isRunningTest={chat.isLoading}
                />
              ))}
            </ChatGrid>
          )}
        </>
      )}

      {blockTestError && <ErrorContainer>{blockTestError}</ErrorContainer>}

      <AddChatModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleAddChat}
      />

      <CopyModal
        isOpen={isCopyModalOpen}
        onClose={() => {
          setIsCopyModalOpen(false);
          setSelectedChatToCopy(null);
        }}
        onSave={handleCreateCopies}
      />
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const TitleSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const FormSection = styled.div`
  background-color: ${COLORS.white};
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
`;

const FormRow = styled.div`
  display: flex;
  gap: 1.5rem;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const DropdownContainer = styled.div`
  width: 300px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const NameInputContainer = styled.div`
  width: 300px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const InputLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: ${COLORS.darkGrey};
`;

const NameInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${COLORS.lightGrey};
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: ${COLORS.violet};
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 1.5rem 0;
  color: ${COLORS.black};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AddChatButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${COLORS.violet};
  font-weight: 500;
  font-size: 0.875rem;
  transition: color 0.2s;

  &:hover {
    color: ${COLORS.darkViolet};
  }

  &:disabled {
    color: ${COLORS.lightGrey};
    cursor: not-allowed;
  }
`;

const PlusSign = styled.span`
  font-size: 1.25rem;
`;

const ChatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (min-width: 1400px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 1200px) and (min-width: 769px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) and (min-width: 481px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  padding: 4rem;
  background-color: ${COLORS.lightViolet};
  border-radius: 0.75rem;
  text-align: center;
`;

const EmptyStateText = styled.p`
  font-size: 1.125rem;
  color: ${COLORS.grey};
`;

const HeadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ErrorContainer = styled.div`
  margin-top: 2rem;
  padding: 1rem;
  background-color: ${COLORS.lightestRed};
  border-left: 3px solid ${COLORS.red};
  border-radius: 0.5rem;
  color: ${COLORS.darkGrey};
`;

const Subtitle = styled.div`
  font-size: 1rem;
  color: ${COLORS.darkGrey};
  margin-top: 0.5rem;
`;

const OrgNameDisplay = styled.div`
  font-size: 1rem;
  padding: 0.75rem;
  background-color: ${COLORS.lightestViolet};
  border-radius: 0.5rem;
  border: 1px solid ${COLORS.lightViolet};
  color: ${COLORS.darkGrey};
`;

const TestNameDisplay = styled.div`
  font-size: 1rem;
  padding: 0.75rem;
  background-color: ${COLORS.lightestViolet};
  border-radius: 0.5rem;
  border: 1px solid ${COLORS.lightViolet};
  color: ${COLORS.darkGrey};
`;
